import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellRing, Send, AlertTriangle, Share } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';
import { enablePush, getPushStatus } from './push';

/** Detect the runtime the user is looking at NEXA in. Result drives the copy
 * shown in the notifications card so we don't just say "not supported". */
const detectEnv = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
    || (/Mac/i.test(ua) && navigator.maxTouchPoints > 1);
  // iOS Safari exposes `navigator.standalone === true` only inside a PWA
  // launched from a Home-Screen icon. In-browser it's `false`/undefined.
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  const hasPush = 'serviceWorker' in navigator && 'PushManager' in window;
  return { isIOS, standalone, hasPush };
};

export const PushCard = () => {
  const [push, setPush] = useState('off');
  const [env, setEnv] = useState({ isIOS: false, standalone: false, hasPush: true });

  useEffect(() => {
    setEnv(detectEnv());
    getPushStatus().then(setPush);
  }, []);

  const onEnablePush = async () => {
    try {
      await enablePush();
      setPush('on');
      toast.success('Notifications enabled — NEXA will ping you at 06:00');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const testPush = async () => {
    try {
      await nexaApi.post('/push/test');
      toast.success('Test notification sent');
    } catch {
      toast.error('No subscription found on the server');
    }
  };

  // Diagnostic message for the "off" state. Priority: standalone > push API > iOS Safari.
  const blockedReason = (() => {
    if (env.isIOS && !env.standalone) {
      return {
        title: 'Open NEXA from your Home Screen icon',
        body: 'You\u2019re currently viewing NEXA in Safari. Push works only when the app is launched from the NEXA icon on your Home Screen \u2014 iOS runs it as a real app then.',
        steps: [
          'Long-press the NEXA icon on your Home Screen',
          'Tap "Delete Bookmark" to remove the old shortcut',
          'Come back to Safari, tap the Share button, then "Add to Home Screen"',
          'Close Safari, then open NEXA from the new icon',
        ],
      };
    }
    if (!env.hasPush) {
      return {
        title: 'Your browser doesn\u2019t support push yet',
        body: 'On iPhone push needs iOS 16.4 or newer. Update iOS in Settings \u203a General \u203a Software Update, then come back.',
        steps: [],
      };
    }
    return null;
  })();

  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-push-card">
      <div className="flex items-center gap-2 mb-3">
        {push === 'on' ? (
          <BellRing className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
        ) : (
          <Bell className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
        )}
        <span className="text-xs tracking-[0.2em] uppercase text-white/40">Notifications</span>
        {env.standalone && push !== 'on' && (
          <span className="ml-auto text-[10px] tracking-widest uppercase text-emerald-300 font-mono">
            Home-Screen OK
          </span>
        )}
      </div>

      {push === 'on' ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-white/60">Morning 06:00 + wind-down 22:00 pushes are on.</p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={testPush}
            data-testid="nexa-push-test"
            className="h-10 px-4 rounded-xl border border-white/15 text-sm text-white/80 flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" /> Test
          </motion.button>
        </div>
      ) : blockedReason ? (
        <div data-testid="nexa-push-blocked">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <p className="text-sm text-white/90 font-medium">{blockedReason.title}</p>
              <p className="text-sm text-white/55 mt-1 leading-relaxed">{blockedReason.body}</p>
            </div>
          </div>
          {blockedReason.steps.length > 0 && (
            <ol className="text-[13px] text-white/70 space-y-1.5 pl-5 list-decimal marker:text-sky-400 mb-4">
              {blockedReason.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          )}
          {env.isIOS && !env.standalone && (
            <div className="flex items-center gap-2 text-[11px] text-white/40 border border-white/10 rounded-xl px-3 py-2">
              <Share className="w-3.5 h-3.5 text-sky-400 shrink-0" strokeWidth={1.8} />
              Look for the square-with-up-arrow icon in Safari&rsquo;s bottom bar.
            </div>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-white/60 mb-4">
            Get the 06:00 morning and 22:00 wind-down pushes.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onEnablePush}
            data-testid="nexa-push-enable"
            className="w-full h-12 rounded-xl bg-white text-black font-semibold"
          >
            Enable notifications
          </motion.button>
        </>
      )}
    </div>
  );
};
